import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Zap, Info, Plus, ChevronDown, Bot, Sparkles } from "lucide-react";
import type { Tool } from "@/types/tool";
import { trackTryToolClick } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import TechStackIcons from "@/components/TechStackIcons";

interface ToolCardProps {
  tool: Tool;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  index: number;
  isPromoted?: boolean;
}

const ToolCard = ({ tool, isSelected, onToggleSelect, isPromoted }: ToolCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const freeTier = tool.pricingTiers.some(tier => tier.price === 0);
  const paidTier = tool.pricingTiers.find(tier => tier.price !== null && tier.price > 0);
  const destination = tool.affiliateUrl || tool.url;

  return (
    <TooltipProvider delayDuration={300}>
      <article
        onClick={(event) => {
          if ((event.target as HTMLElement).closest("a, button") || window.getSelection()?.toString()) return;
          trackTryToolClick(tool.id, tool.name, "card");
          window.open(destination, "_blank", "noopener,noreferrer");
        }}
        className={`group relative rounded-2xl border bg-card p-5 sm:p-6 transition-colors cursor-pointer ${isSelected ? "border-foreground/30 ring-1 ring-foreground/10" : "border-border hover:border-foreground/25"} ${isPromoted ? "border-success/40" : ""}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-secondary border border-border flex items-center justify-center shrink-0">
            <img src={tool.logo} alt={`${tool.name} logo`} loading="lazy" className="w-8 h-8 object-contain"
              onError={(event) => {
                event.currentTarget.style.display = "none";
                const fallback = event.currentTarget.nextElementSibling;
                if (fallback instanceof HTMLElement) fallback.style.display = "flex";
              }} />
            <span className="hidden text-lg font-bold text-foreground">{tool.name.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-foreground leading-snug break-words">{tool.name}</h3>
            <div className="flex items-center flex-wrap gap-2 mt-1 text-[11px]">
              <span className={tool.category === "Agentic" ? "inline-flex items-center gap-1 text-success font-semibold" : "text-muted-foreground"}>
                {tool.category === "Agentic" && <Bot className="w-3 h-3" />}{tool.category}
              </span>
              {isPromoted && <span className="inline-flex items-center gap-1 text-muted-foreground"><Sparkles className="w-3 h-3" />Featured</span>}
            </div>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button asChild variant="ghost" size="icon" className="w-11 h-11 shrink-0 rounded-full text-muted-foreground">
                <Link to={`/tool/${tool.id}`} aria-label={`More details about ${tool.name}`} onClick={event => {
                  event.stopPropagation();
                  sessionStorage.setItem("scrollY", String(window.scrollY));
                }}><Info /></Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>See full details</TooltipContent>
          </Tooltip>
        </div>

        <p className="text-sm sm:text-base text-foreground leading-relaxed mt-4 min-h-[3em]">
          <span className="font-semibold">Best for: </span>{tool.bestFor}
        </p>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 mb-4 text-xs">
          {freeTier && <span className="inline-flex items-center gap-1 text-success font-medium"><Check className="w-3.5 h-3.5" />Free tier available</span>}
          {paidTier && <span className="text-muted-foreground">From ${paidTier.price}/{paidTier.period === "year" ? "yr" : paidTier.period === "one-time" ? "one-time" : "mo"}</span>}
          {!freeTier && !paidTier && <span className="text-muted-foreground">Contact for pricing</span>}
        </div>

        <Button asChild variant="cta" className="w-full h-12 rounded-xl text-[15px]">
          <a href={destination} target="_blank" rel="sponsored noopener noreferrer" data-tool={tool.id}
            aria-label={`Try now: ${tool.name}`} data-lovable-event="try_tool_click"
            onClick={event => { event.stopPropagation(); trackTryToolClick(tool.id, tool.name, "card"); }}>
            Try now <Zap />
          </a>
        </Button>

        <div className="flex items-center justify-between gap-2 mt-2">
          <Button variant="ghost" className="h-11 px-0 text-xs text-muted-foreground hover:bg-transparent" aria-expanded={isExpanded}
            aria-controls={`tool-summary-${tool.id}`} onClick={event => { event.stopPropagation(); setIsExpanded(previous => !previous); }}>
            {isExpanded ? "Show less" : "Show more"}<ChevronDown className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
          </Button>
          <Button variant="ghost" className="h-11 px-2 text-xs text-muted-foreground" aria-pressed={isSelected}
            aria-label={`${isSelected ? "Remove" : "Add"} ${tool.name} ${isSelected ? "from" : "to"} comparison`}
            onClick={event => { event.stopPropagation(); onToggleSelect(tool.id); }}>
            {isSelected ? <Check /> : <Plus />}{isSelected ? "Added" : "Compare"}
          </Button>
        </div>

        {isExpanded && <div id={`tool-summary-${tool.id}`} className="pt-4 mt-1 border-t border-border cursor-default" onClick={event => event.stopPropagation()}>
          <p className="text-sm leading-relaxed text-muted-foreground mb-4">{tool.description}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {tool.pros.map(pro => <p key={pro} className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed"><Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-success" />{pro}</p>)}
            {tool.cons.map(con => <p key={con} className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed"><X className="w-3.5 h-3.5 mt-0.5 shrink-0 text-destructive" />{con}</p>)}
          </div>
          {tool.techStack.length > 0 && <div className="mt-4"><TechStackIcons techStack={tool.techStack} /></div>}
        </div>}
      </article>
    </TooltipProvider>
  );
};

export default ToolCard;
